import { LoadingState } from "@/ds/custom/LoadingState";
import { SectionHeader } from "@/ds/custom/SectionHeader";
import { StatePanel } from "@/ds/custom/StatePanel";
import { Button } from "@/ds/shadcn/components/ui/button";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import type { PaginatedInfiniteQueryResult } from "@/lib/common/PaginatedInfiniteQueryResult";
import { ArrowRightIcon, type LucideIcon } from "lucide-react";
import type { FC, PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router";

const DASHBOARD_SECTION_ITEM_COUNT = 5;

export interface DashboardSectionProps extends PropsWithChildren {
	title: string;
	description: ReactNode;
	href?: string;
}

export const DashboardSection: FC<DashboardSectionProps> = props => {
	const {
		title,
		description,
		href,
		children
	} = props;

	return (
		<section className="-mb-px flex flex-col">
			<SectionHeader title={title} description={description}>
				{
					href && (
						<Button variant="ghost" size="sm" asChild>
							<Link to={href}>
								View all
								<ArrowRightIcon />
							</Link>
						</Button>
					)
				}
			</SectionHeader>
			{children}
		</section>
	);
};

export interface DashboardSectionItemsProps<T> {
	forQuery: PaginatedInfiniteQueryResult<T>;
	getKey: (item: T) => string;
	renderItem: (item: T) => ReactNode;
	emptyIcon: LucideIcon;
	emptyText: string;
	emptyAction?: ReactNode;
	layout?: "List" | "Strip";
}

export const DashboardSectionItems = <T,>(props: DashboardSectionItemsProps<T>): React.JSX.Element => {
	const {
		forQuery,
		getKey,
		renderItem,
		emptyIcon,
		emptyText,
		emptyAction,
		layout = "List"
	} = props;

	if (forQuery.isLoading) {
		return <LoadingState />;
	}

	if (forQuery.error) {
		return (
			<div className="p-3">
				<ErrorDisplay error={forQuery.error} />
			</div>
		);
	}

	const results = forQuery.data?.pages[0]?.results ?? [];
	const items = layout === "List" ? results.slice(0, DASHBOARD_SECTION_ITEM_COUNT) : results;

	if (items.length === 0) {
		return (
			<StatePanel icon={emptyIcon} title={emptyText} compact>
				{emptyAction}
			</StatePanel>
		);
	}

	if (layout === "Strip") {
		return (
			<div className="flex gap-3 overflow-x-auto p-4">
				{
					items.map(x => (
						<div key={getKey(x)} className="w-44 shrink-0 @md:w-52">{renderItem(x)}</div>
					))
				}
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			{
				items.map(x => (
					<div key={getKey(x)} className="border-b">{renderItem(x)}</div>
				))
			}
		</div>
	);
};

export interface DashboardSectionRowProps {
	href: string;
	leading: ReactNode;
	primary: ReactNode;
	secondary: ReactNode;
}

export const DashboardSectionRow: FC<DashboardSectionRowProps> = props => {
	const {
		href,
		leading,
		primary,
		secondary
	} = props;

	return (
		<Link to={href} className="flex min-h-15 items-center gap-3 px-4 py-2 transition-colors duration-150 hover:bg-background-highlight">
			<div className="min-w-0 flex-1">{leading}</div>
			<div className="flex shrink-0 flex-col items-end text-sm">
				<div className="type-item">{primary}</div>
				<div className="text-xs">{secondary}</div>
			</div>
		</Link>
	);
};
